import {
    Box,
    Button,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Text
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import ContentEditable from 'react-contenteditable';

const catalogFields = [
    'Name',
    'Brand',
    'Retailer ID',
    'Description',
    'Short Description',
    'Price',
    'Current Price',
    'Unit Price',
    'Custom Label 0',
    'Custom Label 1',
    'Custom Label 2',
    'Custom Label 3',
    'Custom Label 4',
    'Custom Number 1',
    'Custom Number 2',
    'Custom Number 3',
    'Custom Number 4',
    'URL',
];

const priceTokens = ['product.price', 'product.current_price', 'product.unit_price'];
const customNumberTokens = [
    'product.custom_number_1',
    'product.custom_number_2',
    'product.custom_number_3',
    'product.custom_number_4',
];
const urlTokens = ['product.url'];

const CustomDeepLinkInput = ({ value, onChange, error, placeholder = '', maxW = 'none' }) => {

    const contentRef = useRef(null);
    const [html, setHtml] = useState('');
    const [activeMenu, setActiveMenu] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

    const insertToken = (tokenName) => {
        const wrapper = document.createElement('span');
        wrapper.contentEditable = 'false';
        wrapper.dataset.token = tokenName;
        wrapper.dataset.format = 'catalogue';
        wrapper.style =
            'display:inline-flex;align-items:center;gap:4px;background:#eee;padding:2px 4px;border-radius:4px;margin-right:2px;';

        const label = document.createElement('span');
        label.textContent = `{{${tokenName}}}`;

        const menuTrigger = document.createElement('span');
        menuTrigger.textContent = 'Aa';
        menuTrigger.style =
            'color:#3182ce;font-weight:bold;font-size:12px;cursor:pointer;';
        menuTrigger.className = 'token-format-trigger';

        menuTrigger.addEventListener('mouseenter', () => {
            menuTrigger.style.textDecoration = 'underline';
        });
        menuTrigger.addEventListener('mouseleave', () => {
            menuTrigger.style.textDecoration = 'none';
        });

        setTimeout(() => {
            menuTrigger.addEventListener('click', (e) => {
                console.log(`Clicked on token format for ${tokenName}`);

                e.stopPropagation();
                const rect = e.target.getBoundingClientRect();
                setActiveMenu({
                    token: tokenName,
                    element: wrapper,
                    format: wrapper.dataset.format || 'catalogue',
                });

                setMenuPosition({ x: rect.left, y: rect.bottom });
            });
        }, 0);


        wrapper.appendChild(label);
        wrapper.appendChild(menuTrigger);

        const editableDiv = contentRef.current;

        // Ensure editable div is focused
        editableDiv.focus();

        // If empty, insert a space so range can be set
        if (editableDiv.innerHTML.trim() === '') {
            editableDiv.innerHTML = '<br>';
        }

        const selection = window.getSelection();
        const range = document.createRange();

        if (!selection || selection.rangeCount === 0) {
            // No range — place at end
            range.selectNodeContents(editableDiv);
            range.collapse(false);
        } else {
            // Use current range
            range.setStart(selection.getRangeAt(0).startContainer, selection.getRangeAt(0).startOffset);
            range.setEnd(selection.getRangeAt(0).endContainer, selection.getRangeAt(0).endOffset);
            range.deleteContents();
        }

        range.insertNode(wrapper);
        range.collapse(false);

        selection.removeAllRanges();
        selection.addRange(range);

        extractValue();
    };

    useEffect(() => {
        const container = contentRef.current;
        if (!container) return;

        const handleTriggerClick = (e) => {
            const trigger = e.target.closest('.token-format-trigger');
            if (trigger && trigger.parentElement?.dataset?.token) {
                e.stopPropagation();
                const token = trigger.parentElement.dataset.token;
                const rect = trigger.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();

                setActiveMenu({
                    token,
                    element: trigger.parentElement,
                    format: trigger.parentElement?.dataset?.format || 'catalogue',
                });

                setMenuPosition({
                    x: rect.left - containerRect.left,
                    y: rect.bottom - containerRect.top,
                });
            }
        };

        container.addEventListener('click', handleTriggerClick);
        return () => {
            container.removeEventListener('click', handleTriggerClick);
        };
    }, []);



    const extractValue = () => {
        const div = document.createElement('div');
        div.innerHTML = contentRef.current?.innerHTML || '';
        let text = '';
        div.childNodes.forEach((node) => {
            if (node.nodeType === 3) {
                text += node.textContent;
            } else if (node.dataset?.token) {
                const format = node.dataset.format || 'catalogue';
                const formatPart = format !== 'catalogue' ? `|${format}` : '';
                text += `{{${node.dataset.token}${formatPart}}}`;
            } else {
                text += node.textContent;
            }
        });
    
        const finalText = text.trim();
    
        //  Only trigger onChange if there is meaningful value
        if (finalText.length > 0) {
            onChange(finalText);
        } else {
            return;
        }
    };
    


    const handleKeyDown = (e) => {
        if (e.key === 'Backspace') {
            const selection = window.getSelection();
            if (selection.rangeCount === 0) return;
            const range = selection.getRangeAt(0);
            const container = range.startContainer;

            if (container.nodeType === 3 && range.startOffset === 0) {
                const prev = container.previousSibling;
                if (prev && prev.dataset?.token) {
                    e.preventDefault();
                    prev.remove();
                    extractValue();
                }
            }

            if (container.nodeType === 1 && container.childNodes[range.startOffset - 1]) {
                const prev = container.childNodes[range.startOffset - 1];
                if (prev && prev.dataset?.token) {
                    e.preventDefault();
                    prev.remove();
                    extractValue();
                }
            }
        }
    };

    const handleChange = (e) => {
        setHtml(e.target.value);
    };

    const updateTokenFormat = (formatKey) => {
        if (!activeMenu?.token || !activeMenu?.element) return;

        const wrapper = activeMenu.element;
        const labelSpan = wrapper.querySelector('span');
        if (!labelSpan) return;

        const rawToken = activeMenu.token;
        const baseToken = rawToken.replace(/^product\./, '');

        // Apply formatting label
        let formattedLabel = `{{${baseToken}}}`;

        switch (formatKey) {
            case 'titleize': {
                const words = baseToken.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1));
                formattedLabel = `{{${words.join(' ')}}}`;
                break;
            }
            case 'urlencode': {
                formattedLabel = `{{${baseToken.replace(/_/g, '%20')}}}`;
                break;
            }
            case 'whole': {
                formattedLabel = `{{${baseToken} (Whole)}}`;
                break;
            }
            case 'rounded': {
                formattedLabel = `{{${baseToken} (Rounded)}}`;
                break;
            }
            case 'symbol': {
                formattedLabel = `{{${baseToken} ₹}}`; // or use `$` if needed
                break;
            }
            case 'code': {
                formattedLabel = `{{${baseToken} INR}}`; // replace with dynamic code if needed
                break;
            }
            case 'decimal': {
                formattedLabel = `{{${baseToken} .00}}`;
                break;
            }
            case 'signed': {
                formattedLabel = `{{+/-${baseToken}}}`;
                break;
            }
            default: {
                formattedLabel = `{{${baseToken}}}`;
            }
        }

        wrapper.dataset.format = formatKey;
        labelSpan.textContent = formattedLabel;

        // Update format in state before closing
        setActiveMenu((prev) => ({
            ...prev,
            format: formatKey,
        }));

        extractValue();

        // Delay hiding menu to allow checkmark update (optional smoothness)
        setTimeout(() => {
            setActiveMenu(null);
        }, 100);

    };


    const getTokenOptions = () => {
        const token = activeMenu?.token;
        if (!token) return [];
        if (priceTokens.includes(token)) {
            return [
                { label: 'Whole', value: 'whole' },
                { label: 'Rounded', value: 'rounded' },
                { label: 'Currency Symbol', value: 'symbol' },
                { label: 'Currency Code', value: 'code' },
            ];
        }
        if (customNumberTokens.includes(token)) {
            return [
                { label: 'Decimal', value: 'decimal' },
                { label: 'Signed', value: 'signed' },
            ];
        }
        if (urlTokens.includes(token)) {
            return [{ label: 'URL Encode', value: 'urlencode' }];
        }
        return [
            { label: 'Use Catalogue Format', value: 'catalogue' },
            { label: 'Title Case', value: 'titleize' },
        ];
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            const menu = document.querySelector('.token-format-menu');
            const isTrigger = e.target.closest('.token-format-trigger');
            const isInsideMenu = menu?.contains(e.target);

            // If click is outside both the trigger and the menu, close the menu
            if (!isTrigger && !isInsideMenu) {
                setActiveMenu(null);
            }
        };

        // Use capture phase so it runs before React handlers (more reliable)
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);


    useEffect(() => {
        if (typeof value === 'string') {
            const rebuilt = value.replace(/{{(.*?)}}/g, (_, rawToken) => {
                const [token] = rawToken.split('|');
                return `<span contenteditable="false" data-token="${token}" data-format="catalogue" style="display:inline-flex;align-items:center;gap:4px;background:#eee;padding:2px 4px;border-radius:4px;margin-right:2px;">
                <span>{{${token}}}</span>
                <span class="token-format-trigger" style="color:#3182ce;font-weight:bold;font-size:12px;cursor:pointer;text-decoration:none;">Aa</span>
            </span>`;
            });
            setHtml(rebuilt);
        }
    }, [value]);


    //console.log({ activeMenu });


    return (
        <>
            <Box position="relative" maxWidth={maxW}>
                {!html && (
                <Box
                    position="absolute"
                    top="10px"
                    left="10px"
                    color="gray.400"
                    fontSize="14px"
                    pointerEvents="none"
                    userSelect="none"
                    zIndex="0"
                    >
                        {placeholder ? placeholder : "Enter your content here..." }
                    </Box>
                )}
                
                <ContentEditable
                    innerRef={contentRef}
                    html={html}
                    onChange={handleChange}
                    onBlur={() => {
                        const plainText = contentRef.current?.innerText?.trim();
                        if (plainText) {
                            extractValue();
                        } else {
                            return;
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    tagName="div"
                    style={{
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '10px',
                        paddingRight: '40px',
                        minHeight: '40px',
                        fontSize: '14px',
                        zIndex: '1'
                    }}
                />

                {activeMenu?.token && (
                    <Box
                        className="token-format-menu"
                        position="absolute"
                        top={`${menuPosition.y}px`}
                        left={`${menuPosition.x}px`}
                        bg="white"
                        border="1px solid #ccc"
                        p={2}
                        borderRadius="md"
                        zIndex="99999"
                        boxShadow="lg"
                        minW="160px"
                        maxW="240px"
                        style={{ pointerEvents: 'auto' }}
                    >
                        {getTokenOptions().map((opt) => {
                            // console.log({ opt, activeMenu });

                            return (
                                <Box
                                    key={opt.value}
                                    fontSize="sm"
                                    cursor="pointer"
                                    px={2}
                                    py={1}
                                    _hover={{ bg: 'gray.100' }}
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                    onClick={() => {
                                        updateTokenFormat(opt.value)
                                    }}
                                >
                                    {/* <Box minW="18px" textAlign="center">
                                        {isSelected ? '✓' : ''}
                                    </Box> */}
                                    <Box>{opt.label}</Box>
                                </Box>
                            );
                        })}

                    </Box>

                )}

                <Box position="absolute" top="5px" right="5px">
                    <Menu >
                        <MenuButton as={Button} size="sm">
                            +
                        </MenuButton>
                        <MenuList maxHeight="300px" overflowY="auto" zIndex={2}>
                            {catalogFields.map((field) => {
                                const tokenName = `product.${field.toLowerCase().replace(/ /g, '_')}`;
                                return (
                                    <MenuItem key={field} onClick={() => insertToken(tokenName)} fontSize="12px" >
                                        {field}
                                    </MenuItem>
                                );
                            })}
                        </MenuList>
                    </Menu>
                </Box>
            </Box>
            {error && (
                <Text mt="1" fontSize="xs" color="red.500">
                    {error}
                </Text>
            )}
        </>

    );
};

export default CustomDeepLinkInput;